import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase-client';
import { projectId, publicAnonKey } from '../../../utils/supabase/info';

export interface RealtimeVariant {
  id: string;
  variantNumber: number;
  status: 'pending' | 'completed' | 'failed';
  gcsPath?: string;
  signedUrl?: string;
  aiProvider?: string;
  errorMessage?: string;
  generationTimeMs?: number;
  outputImageId?: string;
}

export interface GenerationRealtimeState {
  variants: RealtimeVariant[];
  generationStatus: 'processing' | 'completed' | 'failed' | 'cancelled' | 'retrying' | null;
  variantsExpected: number;
  isTimeout: boolean;
}

interface UseGenerationRealtimeOptions {
  onAllCompleted?: (variants: RealtimeVariant[]) => void;
  onAllFailed?: () => void;
  onTimeout?: () => void;
}

const POLL_INTERVAL_MS = 5_000;
const TIMEOUT_MS = 180_000; // 3 minutes

export function useGenerationRealtime(
  generationId: string | null,
  options?: UseGenerationRealtimeOptions,
) {
  const [state, setState] = useState<GenerationRealtimeState>({
    variants: [],
    generationStatus: null,
    variantsExpected: 2,
    isTimeout: false,
  });

  const startedAtRef = useRef<number | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const completedCallbackRef = useRef(false);

  // Reconcile state from DB (poll fallback + initial load)
  const reconcileFromDB = useCallback(async (genId: string) => {
    try {
      // Fetch generation status
      // Cast needed: variant_count column added by async_generation migration
      const { data: gen } = await supabase
        .from('generations')
        .select('status, variant_count')
        .eq('id', genId)
        .single() as { data: { status: string; variant_count: number } | null; error: any };

      if (!gen) return;

      // Fetch all outputs for this generation
      // Cast needed: status, gcs_path, signed_url, error_message columns added by async_generation migration
      const { data: outputs } = await supabase
        .from('generation_outputs')
        .select('id, variant_number, status, gcs_path, signed_url, ai_provider, error_message, generation_time_ms, output_image_id')
        .eq('generation_id', genId)
        .order('variant_number') as { data: Array<{
          id: string; variant_number: number; status: string;
          gcs_path: string | null; signed_url: string | null;
          ai_provider: string | null; error_message: string | null;
          generation_time_ms: number | null; output_image_id: string | null;
        }> | null; error: any };

      const variants: RealtimeVariant[] = (outputs || []).map(o => ({
        id: o.id,
        variantNumber: o.variant_number,
        status: o.status as RealtimeVariant['status'],
        gcsPath: o.gcs_path || undefined,
        signedUrl: o.signed_url || undefined,
        aiProvider: o.ai_provider || undefined,
        errorMessage: o.error_message || undefined,
        generationTimeMs: o.generation_time_ms || undefined,
        outputImageId: o.output_image_id || undefined,
      }));

      setState(prev => ({
        ...prev,
        variants,
        generationStatus: gen.status as GenerationRealtimeState['generationStatus'],
        variantsExpected: gen.variant_count || 2,
      }));

      return { status: gen.status, variants };
    } catch (err) {
      console.error('[useGenerationRealtime] reconcile error:', err);
      return null;
    }
  }, []);

  // Cancel generation
  const cancel = useCallback(async () => {
    if (!generationId) return false;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return false;

      const supabaseUrl = `https://${projectId}.supabase.co`;
      const response = await fetch(`${supabaseUrl}/functions/v1/generate-render/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': publicAnonKey,
        },
        body: JSON.stringify({ generationId }),
      });

      const result = await response.json();

      if (result.success) {
        setState(prev => ({ ...prev, generationStatus: 'cancelled' }));
      }

      return result.success;
    } catch (err) {
      console.error('[useGenerationRealtime] cancel error:', err);
      return false;
    }
  }, [generationId]);

  // Retry failed variant
  const retry = useCallback(async () => {
    if (!generationId) return false;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return false;

      const supabaseUrl = `https://${projectId}.supabase.co`;
      const response = await fetch(`${supabaseUrl}/functions/v1/generate-render/retry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': publicAnonKey,
        },
        body: JSON.stringify({ generationId }),
      });

      const result = await response.json();

      if (result.success) {
        setState(prev => ({ ...prev, generationStatus: 'retrying' }));
      }

      return result.success;
    } catch (err) {
      console.error('[useGenerationRealtime] retry error:', err);
      return false;
    }
  }, [generationId]);

  // Reset when generationId changes
  useEffect(() => {
    if (!generationId) {
      setState({
        variants: [],
        generationStatus: null,
        variantsExpected: 2,
        isTimeout: false,
      });
      completedCallbackRef.current = false;
      return;
    }

    startedAtRef.current = Date.now();
    completedCallbackRef.current = false;

    setState({
      variants: [],
      generationStatus: 'processing',
      variantsExpected: 2,
      isTimeout: false,
    });

    // Initial load from DB
    reconcileFromDB(generationId);

    // --- Realtime subscription ---
    const channel = supabase
      .channel(`generation_outputs:${generationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'generation_outputs',
          filter: `generation_id=eq.${generationId}`,
        },
        (payload) => {
          const record = payload.new as any;
          const variant: RealtimeVariant = {
            id: record.id,
            variantNumber: record.variant_number,
            status: record.status || 'completed',
            gcsPath: record.gcs_path || undefined,
            signedUrl: record.signed_url || undefined,
            aiProvider: record.ai_provider || undefined,
            errorMessage: record.error_message || undefined,
            generationTimeMs: record.generation_time_ms || undefined,
            outputImageId: record.output_image_id || undefined,
          };

          setState(prev => {
            // Deduplicate by variant_number
            const existing = prev.variants.findIndex(v => v.variantNumber === variant.variantNumber);
            const newVariants = existing >= 0
              ? prev.variants.map((v, i) => i === existing ? variant : v)
              : [...prev.variants, variant];

            return { ...prev, variants: newVariants };
          });
        }
      )
      .subscribe();

    // --- Poll fallback (reconcile every 5s) ---
    pollTimerRef.current = setInterval(async () => {
      const result = await reconcileFromDB(generationId);
      if (result && ['completed', 'failed', 'cancelled'].includes(result.status)) {
        // Generation finalized — stop polling
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      }
    }, POLL_INTERVAL_MS);

    // --- Safety timeout ---
    timeoutTimerRef.current = setTimeout(() => {
      setState(prev => {
        if (['processing', 'retrying'].includes(prev.generationStatus || '')) {
          return { ...prev, isTimeout: true };
        }
        return prev;
      });
      options?.onTimeout?.();
    }, TIMEOUT_MS);

    // Cleanup
    return () => {
      supabase.removeChannel(channel);
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);
    };
  }, [generationId, reconcileFromDB]); // eslint-disable-line react-hooks/exhaustive-deps

  // Trigger callbacks when generation completes
  useEffect(() => {
    if (completedCallbackRef.current) return;

    const { generationStatus, variants } = state;

    if (generationStatus === 'completed' || generationStatus === 'failed') {
      completedCallbackRef.current = true;

      // Stop timers
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);

      const successVariants = variants.filter(v => v.status === 'completed');
      if (successVariants.length > 0) {
        options?.onAllCompleted?.(variants);
      } else {
        options?.onAllFailed?.();
      }
    }
  }, [state.generationStatus, state.variants]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    variants: state.variants,
    generationStatus: state.generationStatus,
    variantsExpected: state.variantsExpected,
    isTimeout: state.isTimeout,
    completedVariants: state.variants.filter(v => v.status === 'completed'),
    failedVariants: state.variants.filter(v => v.status === 'failed'),
    cancel,
    retry,
  };
}
